import { Component, EventEmitter, Inject, Input, Output, ViewChild, AfterViewInit } from '@angular/core';
import { NgModel } from '@angular/forms';
import { BASE_ICM_URL } from 'src/app/injection-tokens';
import { ScsnCustomersService } from '../scsn-customers.service';
import { ExtendedSCSNCustomer } from '../extended-scsn-customer.model';
import { EmptySCSNCustomer } from '../scsn-customer.model';
import { CustomerInfo } from '../customer-info.model';
import { CustomerInformationComponent } from './customer-information/customer-information.component';

@Component({
  selector: 'app-scsn-customers-form',
  templateUrl: './scsn-customers-form.component.html',
  styleUrls: ['./scsn-customers-form.component.scss'],
})
export class ScsnCustomersFormComponent implements AfterViewInit {
  @Input() extSCSNCustomers!: ExtendedSCSNCustomer[];
  @Output() sendCreate = new EventEmitter<ExtendedSCSNCustomer>();
  @Output() sendUpdate = new EventEmitter<ExtendedSCSNCustomer>();

  @ViewChild(CustomerInformationComponent) customerInformation!: CustomerInformationComponent;
  @ViewChild('indiCustomerNumber') indiCustomerNumber!: NgModel;
  @ViewChild('gln') gln!: NgModel;
  @ViewChild('enabled') enabled!: NgModel;
  @ViewChild('ublViaSCSN') ublViaSCSN!: NgModel;

  extSCSNCustomer: ExtendedSCSNCustomer = { scsnCustomer: EmptySCSNCustomer() };
  companyName: string = '';
  showError: boolean = false;
  showCustomerInfo: boolean = false;
  showGlnUrl: boolean = false;
  createMode: boolean = true;

  private viewInitialized = false;

  constructor(
    private scsnCustomerService: ScsnCustomersService,
    @Inject(BASE_ICM_URL) readonly baseICMUrl: string
  ) {}

  ngAfterViewInit() {
    this.viewInitialized = true;
  }

  getCustomerInfo(): void {
    if (this.indiCustomerNumber.valid) {
      this.showCustomerInfo = true;
      this.customerInformation.setIsLoading(true);

      setTimeout(() => {
        this.extSCSNCustomer.scsnCustomer.indiCustomerNumber =
          this.extSCSNCustomer.scsnCustomer.indiCustomerNumber.trim();

        this.scsnCustomerService.getCustomerInfo(this.extSCSNCustomer.scsnCustomer.indiCustomerNumber).subscribe({
          next: (customerInfo: CustomerInfo | null) => {
            if (!customerInfo) {
            } else {
              this.extSCSNCustomer.customerInfo = customerInfo;
              this.showError = false;
            }
          },
          error: (error: Error) => {
            this.showError = true;
            console.error(error);
          },
        });
        this.customerInformation.setIsLoading(false);
      }, 3000); // 3000 milliseconds = 3 seconds break so people can see my awesome loading icon
    }
  }

  hideCustomerInfo(): void {
    this.showCustomerInfo = false;
  }

  updateGLNUrlOnBlur(): void {
    this.extSCSNCustomer.scsnCustomer.gln = this.extSCSNCustomer.scsnCustomer.gln.trim();
    if (this.gln.valid) {
      this.showGlnUrl = true;
    }
  }

  setEditSCSNCustomer(extSCSNCustomer: ExtendedSCSNCustomer): void {
    this.extSCSNCustomer = JSON.parse(JSON.stringify(extSCSNCustomer)); // Making a copy so the table doesn't get a fake update
    this.companyName = extSCSNCustomer.customerInfo ? extSCSNCustomer.customerInfo.companyName : '';
    this.showCustomerInfo = true;
    this.createMode = false;
  }

  sendCreateOrUpdate(extSCSNCustomer: ExtendedSCSNCustomer): void {
    extSCSNCustomer.scsnCustomer.indiCustomerNumber = extSCSNCustomer.scsnCustomer.indiCustomerNumber.replace(
      /\s/g,
      ''
    );

    if (this.createMode) {
      this.sendCreate.emit(extSCSNCustomer);
    } else {
      this.sendUpdate.emit(extSCSNCustomer);
    }
    this.reset();
  }

  reset(): void {
    this.extSCSNCustomer = { scsnCustomer: EmptySCSNCustomer() };
    this.indiCustomerNumber.reset();
    this.gln.reset();
    this.createMode = true;
    this.showCustomerInfo = false;
    this.showGlnUrl = false;
  }

  isFormValid(): boolean {
    return this.isIndiCustomerNumberValid() && this.isGLNValid();
  }

  isIndiCustomerNumberValid(): boolean {
    if (!this.viewInitialized) {
      return false;
    }

    if (this.indiCustomerNumber.valid == null) {
      return false;
    }

    return this.indiCustomerNumber.valid && this.isCustomerNumberUnique();
  }

  isGLNValid(): boolean {
    if (this.gln.valid == null) {
      return false;
    }

    return this.gln.valid && this.isGLNUnique();
  }

  isCustomerNumberUnique(): boolean {
    const foundSCSNCustomer: ExtendedSCSNCustomer | undefined = this.extSCSNCustomers.find(customer => {
      if (this.createMode) {
        return customer.scsnCustomer.indiCustomerNumber === this.extSCSNCustomer.scsnCustomer.indiCustomerNumber;
      } else {
        return (
          customer.scsnCustomer.indiCustomerNumber === this.extSCSNCustomer.scsnCustomer.indiCustomerNumber &&
          customer.scsnCustomer.id != this.extSCSNCustomer.scsnCustomer.id
        );
      }
    });
    return foundSCSNCustomer == undefined;
  }

  isGLNUnique(): boolean {
    const foundSCSNCustomer: ExtendedSCSNCustomer | undefined = this.extSCSNCustomers.find(customer => {
      if (this.createMode) {
        return customer.scsnCustomer.gln === this.extSCSNCustomer.scsnCustomer.gln;
      } else {
        return (
          customer.scsnCustomer.gln === this.extSCSNCustomer.scsnCustomer.gln &&
          customer.scsnCustomer.id != this.extSCSNCustomer.scsnCustomer.id
        );
      }
    });
    return foundSCSNCustomer == undefined;
  }
}
